/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import {
  Editor,
  EditorState,
  RichUtils,
  ContentState,
  convertFromHTML,
  convertToRaw,
} from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import 'draft-js/dist/Draft.css';
import { Box } from '@mui/material';

const TextEditorDraft = ({
  name,
  error,
  label,
  value,
  touched,
  required,
  className,
  minHeight,
  placeholder,
  handleChange,
}) => {
  const editorRef = useRef(null);

  const contentState = value
    ? ContentState.createFromBlockArray(convertFromHTML(value))
    : ContentState.createFromText('');
  const [editorState, setEditorState] = useState(EditorState.createWithContent(contentState));

  useEffect(() => {
    const currentContent = editorState.getCurrentContent();
    const rawContent = convertToRaw(currentContent);

    const removeStyleAttributes = () => {
      rawContent.blocks.forEach(block => {
        block.inlineStyleRanges = [];
      });
    };

    const addNoFollowToLinks = () => {
      rawContent.entityMap = Object.fromEntries(
        Object.entries(rawContent.entityMap).map(([key, entity]) => {
          if (entity.type === 'LINK') {
            return [
              key,
              {
                ...entity,
                data: {
                  ...entity.data,
                  target: '_blank',
                  rel: 'nofollow noopener noreferrer',
                },
              },
            ];
          }
          return [key, entity];
        }),
      );
    };

    removeStyleAttributes();
    addNoFollowToLinks();
  }, [editorState]);

  const handleEditorChange = state => {
    setEditorState(state);

    const currentContent = state.getCurrentContent();
    const htmlContent = stateToHTML(currentContent);

    handleChange(htmlContent);
  };

  const handleKeyCommand = command => {
    const newState = RichUtils.handleKeyCommand(editorState, command);

    if (newState) {
      setEditorState(newState);

      return true;
    }

    return false;
  };

  const applyInlineStyle = style => {
    setEditorState(RichUtils.toggleInlineStyle(editorState, style));
  };

  const applyBlockType = blockType => {
    setEditorState(RichUtils.toggleBlockType(editorState, blockType));
  };

  return (
    <div
      className={`form-outline mb-4 DashBoardInputBx DashBoardCreatBx APDraftEditor${className ? ` ${className}` : ''}`}
    >
      <label id={name} className='form-label' htmlFor={name}>
        {label}
        {required && <span className='RedStar'>*</span>}
      </label>
      <Box
        sx={{
          padding: '8px',
          borderRadius: '4px',
          border: '1px solid #ccc',
        }}
      >
        <div className='toolbar'>
          <button type='button' onClick={() => applyInlineStyle('BOLD')}>
            <FormatBoldIcon />
          </button>
          <button type='button' onClick={() => applyInlineStyle('ITALIC')}>
            <FormatItalicIcon />
          </button>
          <button type='button' onClick={() => applyInlineStyle('UNDERLINE')}>
            <FormatUnderlinedIcon />
          </button>
          <button type='button' onClick={() => applyBlockType('unordered-list-item')}>
            <FormatListBulletedIcon />
          </button>
          <button type='button' onClick={() => applyBlockType('ordered-list-item')}>
            <FormatListNumberedIcon />
          </button>
        </div>
        <Box
          sx={{ minHeight: minHeight || '240px' }}
          onClick={() => {
            if (editorRef.current) {
              editorRef.current.focus();
            }
          }}
        >
          <Editor
            ref={editorRef}
            editorState={editorState}
            placeholder={placeholder}
            onChange={handleEditorChange}
            handleKeyCommand={handleKeyCommand}
          />
        </Box>
      </Box>
      {touched && error && <div className='text-danger'>{error}</div>}
    </div>
  );
};

export default TextEditorDraft;
